import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import dayjs from 'dayjs';

import { isPresent } from 'services/operators';
import { DATE_FORMAT } from 'models/constants/input.constants';
import { ApplicationConfigService } from 'services/application-config.service';
import { createRequestOption } from 'app/shared/util/request-util';
import { BasketOrder } from '../models/basket-order.model';

export type EntityResponseType = HttpResponse<BasketOrder>;
export type EntityArrayResponseType = HttpResponse<BasketOrder[]>;

@Injectable({ providedIn: 'root' })
export class BasketOrderService {
  protected resourceUrl: string;

  constructor(protected http: HttpClient, protected applicationConfigService: ApplicationConfigService) {
    this.resourceUrl = this.applicationConfigService.getEndpointFor('api/basket-orders');
  }

  create(basketOrder: BasketOrder): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(basketOrder);
    return this.http.post<BasketOrder>(this.resourceUrl, copy, { observe: 'response' }).pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  update(basketOrder: BasketOrder): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(basketOrder);
    return this.http
      .put<BasketOrder>(`${this.resourceUrl}/${basketOrder.id}`, copy, { observe: 'response' })
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  partialUpdate(basketOrder: BasketOrder): Observable<EntityResponseType> {
    const copy = this.convertDateFromClient(basketOrder);
    return this.http
      .patch<BasketOrder>(`${this.resourceUrl}/${basketOrder.id}`, copy, { observe: 'response' })
      .pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  find(id: number): Observable<EntityResponseType> {
    return this.http.get<BasketOrder>(`${this.resourceUrl}/${id}`, { observe: 'response' }).pipe(map((res: EntityResponseType) => this.convertDateFromServer(res)));
  }

  query(req?: any): Observable<EntityArrayResponseType> {
    const options = createRequestOption(req);
    return this.http
      .get<BasketOrder[]>(this.resourceUrl, { params: options, observe: 'response' })
      .pipe(map((res: EntityArrayResponseType) => this.convertDateArrayFromServer(res)));
  }

  delete(id: number): Observable<HttpResponse<{}>> {
    return this.http.delete(`${this.resourceUrl}/${id}`, { observe: 'response' });
  }

  addBasketOrderToCollectionIfMissing(basketOrderCollection: BasketOrder[], ...basketOrdersToCheck: (BasketOrder | null | undefined)[]): BasketOrder[] {
    const basketOrders: BasketOrder[] = basketOrdersToCheck.filter(isPresent);
    if (basketOrders.length > 0) {
      const basketOrderCollectionIdentifiers = basketOrderCollection.map(basketOrderItem => basketOrderItem.id);
      const basketOrdersToAdd = basketOrders.filter(basketOrderItem => {
        const basketOrderIdentifier = basketOrderItem.id;
        if (basketOrderIdentifier == null || basketOrderCollectionIdentifiers.includes(basketOrderIdentifier)) {
          return false;
        }
        basketOrderCollectionIdentifiers.push(basketOrderIdentifier);
        return true;
      });
      return [...basketOrdersToAdd, ...basketOrderCollection];
    }
    return basketOrderCollection;
  }

  protected convertDateFromClient(basketOrder: BasketOrder): BasketOrder {
    return Object.assign({}, basketOrder, {
      billDate: basketOrder.billDate?.isValid() ? basketOrder.billDate.format(DATE_FORMAT) : undefined,
      dueDate: basketOrder.dueDate?.isValid() ? basketOrder.dueDate.format(DATE_FORMAT) : undefined,
    });
  }

  protected convertDateFromServer(res: EntityResponseType): EntityResponseType {
    if (res.body) {
      res.body.billDate = res.body.billDate ? dayjs(res.body.billDate) : undefined;
      res.body.dueDate = res.body.dueDate ? dayjs(res.body.dueDate) : undefined;
    }
    return res;
  }

  protected convertDateArrayFromServer(res: EntityArrayResponseType): EntityArrayResponseType {
    if (res.body) {
      res.body.forEach((basketOrder: BasketOrder) => {
        basketOrder.billDate = basketOrder.billDate ? dayjs(basketOrder.billDate) : undefined;
        basketOrder.dueDate = basketOrder.dueDate ? dayjs(basketOrder.dueDate) : undefined;
      });
    }
    return res;
  }
}
